import { useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import SectionTitle from '../common/SectionTitle';
import { get } from '../../utils/api';
import { getUserLanguage } from '../../utils/language';

const TeamSection = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Add state to track which member is being hovered
  const [hoveredMember, setHoveredMember] = useState(null);
  const language = getUserLanguage();
  
  const { ref: sectionRef, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });
  
  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        const headers = { 'accept-language': language };
        const data = await get('/team-members', headers);
        console.log('[TeamSection] Raw team members data:', data);
        
        const activeMembers = data.filter(member => member.is_active);
        const sortedMembers = activeMembers.sort((a, b) => a.display_order - b.display_order);
        
        // Add debug logging for image paths
        sortedMembers.forEach(member => {
          console.log(`[TeamSection] Member ID ${member.id} image path:`, member.image_path);
        });
        
        setTeamMembers(sortedMembers);
      } catch (error) {
        console.error('[TeamSection] Error fetching team members:', error);
        setError(language === 'cs' ? 'Nepodařilo se načíst členy týmu' : 'Failed to load team members');
      } finally {
        setLoading(false);
      }
    };
    fetchTeamMembers();
  }, [language]);
  
  if (loading) {
    return (
      <section id="team" className="py-20 bg-gradient-to-b from-black to-dark-gray">
        <div className="container mx-auto px-4 sm:px-6">
          <SectionTitle
            badge={language === 'cs' ? "Náš tým" : "Our team"}
            title={language === 'cs' ? "Profesionální" : "Professional"}
            highlightedText={language === 'cs' ? "drift tým" : "drift team"}
            description={language === 'cs' ? "Načítání..." : "Loading..."}
          />
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red"></div>
          </div>
        </div>
      </section>
    );
  }
  
  if (error) {
    return (
      <section id="team" className="py-20 bg-gradient-to-b from-black to-dark-gray">
        <div className="container mx-auto px-4 sm:px-6">
          <SectionTitle
            badge={language === 'cs' ? "Náš tým" : "Our team"}
            title={language === 'cs' ? "Profesionální" : "Professional"}
            highlightedText={language === 'cs' ? "drift tým" : "drift team"}
            description={error}
          />
        </div>
      </section>
    );
  }
  
  const descriptions = {
    cs: "Seznamte se s členy našeho profesionálního týmu, kteří reprezentují NO RISK NO DRIFT na závodech a exhibicích po celé Evropě.",
    en: "Meet the members of our professional team who represent NO RISK NO DRIFT at races and exhibitions across Europe."
  };
  
  // Handler functions for mouse events
  const handleMouseEnter = (memberId) => {
    setHoveredMember(memberId);
  };
  
  const handleMouseLeave = () => {
    setHoveredMember(null);
  };

  // Debug function to log image errors
  const handleImageError = (memberId, imagePath) => {
    console.error(`[TeamSection] Image load error for member ID ${memberId}:`, imagePath);
  };
  
  return (
    <section id="team" className="py-20 bg-gradient-to-b from-black to-dark-gray" ref={sectionRef}>
      <div className="container mx-auto px-4 sm:px-6">
        <SectionTitle
          badge={language === 'cs' ? "Náš tým" : "Our team"}
          title={language === 'cs' ? "Profesionální" : "Professional"}
          highlightedText={language === 'cs' ? "drift tým" : "drift team"}
          description={descriptions[language] || descriptions.en}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {teamMembers.map((member, index) => {
            const memberData = member.translations && member.translations[language]
              ? member.translations[language]
              : (member.translations && member.translations.en
                ? member.translations.en
                : { name: '', position: '', description: '' });
            
            // Check if this member is being hovered
            const isHovered = hoveredMember === member.id;
            
            // Use local public folder for all environments since uploads folder is empty
            const imageUrl = member.image_path 
              ? (member.image_path.startsWith('/images/') 
                  ? member.image_path 
                  : `/images/team/${member.image_path}`)
              : '/images/team/placeholder.jpeg';
            
            console.log(`[TeamSection] Rendering member ID ${member.id}, image URL:`, imageUrl);
            
            return (
              <div
                key={member.id}
                className={`drift-card ${inView ? 'animate-fade-in' : 'opacity-0'}`}
                style={{ animationDelay: `${0.1 * index}s` }}
                onMouseEnter={() => handleMouseEnter(member.id)}
                onMouseLeave={handleMouseLeave}
                onTouchStart={() => handleMouseEnter(member.id)}
                onTouchEnd={handleMouseLeave}
              >
                <div className="relative overflow-hidden h-96 w-full">
                  {/* Image container */}
                  <div className="absolute inset-0">
                    <img
                      src={imageUrl}
                      alt={`${memberData.name} - ${memberData.position}`}
                      className={`h-full w-full object-cover transition-transform duration-500 ${isHovered ? 'scale-110' : ''}`}
                      loading="lazy"
                      onError={(e) => {
                        handleImageError(member.id, imageUrl);
                        e.target.onerror = null;
                        e.target.src = '/images/team/placeholder.jpeg';
                      }}
                    />
                  </div>
                  
                  {/* Dark overlay - darker when hovered */}
                  <div 
                    className={`absolute inset-0 bg-black transition-opacity duration-300 ${
                      isHovered ? 'opacity-70' : 'opacity-50'
                    }`}
                  ></div>
                  
                  {/* Content container */}
                  <div className="absolute inset-0 flex flex-col justify-end p-6">
                    {/* Name and position - always visible */}
                    <h3 className="text-xl font-title font-bold text-white mb-1">{memberData.name || ''}</h3>
                    <p className="text-red-500 text-sm font-semibold uppercase mb-3">{memberData.position || ''}</p>
                    
                    {/* Description - only visible when hovered */}
                    {isHovered && (
                      <div className="text-gray-300 text-sm">{memberData.description || ''}</div>
                    )}
                  </div>
                  
                  {/* Social media links - only visible when hovered */}
                  {isHovered && (
                    <div className="absolute top-4 right-4 flex flex-col space-y-2">
                      {member.instagram_url && (
                        <a
                          href={member.instagram_url}
                          className="w-8 h-8 bg-white/10 hover:bg-red-600 rounded flex items-center justify-center text-white transition-colors"
                          target="_blank"
                          rel="noopener"
                        >
                          <i className="fab fa-instagram"></i>
                        </a>
                      )}
                      {member.facebook_url && (
                        <a
                          href={member.facebook_url}
                          className="w-8 h-8 bg-white/10 hover:bg-red-600 rounded flex items-center justify-center text-white transition-colors"
                          target="_blank"
                          rel="noopener"
                        >
                          <i className="fab fa-facebook-f"></i>
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default TeamSection;